import FormControl from "@mui/material/FormControl";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import styled from "styled-components";

// Imported assets
import {
  CloseIcon,
  CopyFileIcon,
  EditIcon,
  RefreshIcon,
  SearchIcon,
} from "../../assets/icons/svgs/Icons";

// Imported components
import NavigationBar from "../../components/nav/NavigationBar";
import NewConnectorModal from "./components/NewConnectorModal";
import PrimaryButton from "../../components/ui/PrimaryButton";
import SelectFeedModal from "./components/SelectFeedModal";

// Styled components
const Container = styled.div`
  align-items: stretch;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: clamp(60px, 5vw, 80px) clamp(50px, 5vw, 100px) 0;
  position: relative;
`;

const ContentModeButton = styled(PrimaryButton)`
  background: ${({ $isSelected }) =>
    $isSelected ? "var(--terbine-blue)" : "#D9D9D9"};
  color: ${({ $isSelected }) => ($isSelected ? "white" : "black")};
  height: 40px;
  padding: 0 20px;
  width: auto;

  &:hover {
    background: lab(
      from
        ${({ $isSelected }) =>
          $isSelected ? "var(--terbine-blue)" : "#D9D9D9"}
        calc(l - 5) a b / 1
    );
  }
`;

const APIButton = styled(ContentModeButton)`
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
`;

const ConnectorsButton = styled(ContentModeButton)`
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
`;

const ContentControls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 10px 0;
`;

const ContentModeButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
`;

const SearchAndNewGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

const TableRowIcon = styled.img`
  cursor: pointer;
  height: auto;
  width: 16px;
`;

const apiTableTestData = [
  {
    type: "TestJava SDK",
    status: "Offline",
    apiToken: "02541d61-daab-48ee-9853-dea02e3b7bba",
  },
  {
    type: "TestPython SDK",
    status: "Online",
    apiToken: "f1dd1d21-e823-4c65-9958-2c0017e369a1",
  },
  {
    type: "TestJavascript SDK",
    status: "Offline",
    apiToken: "e4a859f9-6c92-40b0-ac19-43f93bb1a745",
  },
];

const connectorsTableTestData = [
  {
    name: "ConnectorName1",
    type: "Kafka",
    created: "04/01/2025",
    modified: "04/14/2025",
    status: "Offline",
    configDetails: {
      host: "http://localhost",
      port: "0001",
      topic: "test_input_station",
    },
    selection: "Inbound",
    validity: "Valid",
  },
  {
    name: "ConnectorName2",
    type: "S3",
    created: "04/01/2025",
    modified: "04/14/2025",
    status: "Online",
    configDetails: {
      host: "123.33.33.44",
      port: "0002",
      topic: "sensor_event",
    },
    selection: "Inbound",
    validity: "Valid",
  },
  {
    name: "ConnectorName3",
    type: "JMS",
    created: "04/01/2025",
    modified: "04/14/2025",
    status: "Offline",
    configDetails: {
      host: "http://localhost",
      port: "0003",
      topic: "test_3",
    },
    selection: "Inbound",
    validity: "Invalid",
  },
];

const Technical = () => {
  const [contentMode, setContentMode] = useState("Connectors");

  const [search, setSearch] = useState("");

  return (
    <Container>
      <NavigationBar />

      <Typography
        align="center"
        gutterBottom
        variant="h4"
        sx={{
          fontFamily: "Oxygen, Inter, Roboto, sans-serif",
          padding: "clamp(30px, 5vw, 60px) 0 0 0",
        }}
      >
        Technical
      </Typography>

      <ContentControls>
        <ContentModeButtons>
          <ConnectorsButton
            $isSelected={contentMode === "Connectors"}
            onClick={() => setContentMode("Connectors")}
          >
            CONNECTORS
          </ConnectorsButton>
          <APIButton
            $isSelected={contentMode === "API"}
            onClick={() => setContentMode("API")}
          >
            API
          </APIButton>
        </ContentModeButtons>

        <SearchAndNewGroup>
          <FormControl>
            <TextField
              onChange={(e) => {
                // setTablePage(0);
                setSearch(e.target.value);
              }}
              placeholder="Search"
              size="small"
              slotProps={{
                input: {
                  startAdornment: <SearchIcon />,
                  // endAdornment: search ? (
                  //   <Icon
                  //     onClick={() => setSearch("")}
                  //     src={CancelIcon}
                  //     style={{
                  //       cursor: "pointer",
                  //       marginLeft: "10px",
                  //       marginRight: "0",
                  //     }}
                  //   />
                  // ) : (
                  //   <Icon style={{ marginLeft: "10px", marginRight: "0" }} />
                  // ),
                },
              }}
              value={search}
            />
          </FormControl>
          <NewConnectorModal />
        </SearchAndNewGroup>
      </ContentControls>

      {contentMode === "Connectors" && (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
            "& *": {
              fontFamily: "Inter, Oxygen, Roboto, sans-serif",
            },
          }}
        >
          <Table
            sx={{
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--terbine-blue)",
                  "& th": {
                    color: "white",
                    fontSize: "clamp(12px, 1.5vw, 18px)",
                    fontWeight: 300,
                    borderBottom: "none",
                  },
                  borderBottom: "none",
                }}
              >
                {[
                  { label: "Connector Name", width: "15%" },
                  { label: "Connector Type", width: "15%" },
                  { label: "Created Date", width: "9%" },
                  { label: "Last Modified", width: "9%" },
                  { align: "center", label: "Status", width: "9%" },
                  { label: "Configuration Details" },
                  { label: "Selection", width: "9%" },
                ].map((header, i) => (
                  <TableCell
                    align={header.align || "left"}
                    key={i}
                    width={header.width || null}
                  >
                    {header.label}
                  </TableCell>
                ))}
                <TableCell width="12%" />
                <TableCell width="3%" />
                <TableCell width="3%" />
              </TableRow>
            </TableHead>
            <TableBody>
              {connectorsTableTestData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: "#F2F2F2",
                    borderBottom:
                      index !== connectorsTableTestData.length - 1
                        ? "3px solid white"
                        : "none",
                    borderTop: "none",
                    "& > td": {
                      borderTop: "none",
                      borderBottom:
                        index !== connectorsTableTestData.length - 1
                          ? "3px solid white"
                          : "none",
                      color: "black",
                    },
                  }}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.created}</TableCell>
                  <TableCell>{row.modified}</TableCell>
                  <TableCell align="center">
                    <div
                      style={{
                        background: `${
                          row.status === "Online" ? "limegreen" : "white"
                        }`,
                        border: `${
                          row.status === "Offline" ? "1px solid #D9D9D9" : null
                        }`,
                        borderRadius: "50%",
                        height: "16px",
                        margin: "auto",
                        width: "16px",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <p style={{ margin: 0 }}>
                        Host: {row.configDetails.host}
                      </p>
                      <p style={{ margin: 0 }}>
                        Port: {row.configDetails.port}
                      </p>
                      <p style={{ margin: 0 }}>
                        Topic: {row.configDetails.topic}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{row.selection}</TableCell>
                  <TableCell>Connector {row.validity}</TableCell>
                  <TableCell>
                    <div style={{ cursor: "pointer" }}>
                      <EditIcon />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ cursor: "pointer" }}>
                      <CloseIcon />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {contentMode === "API" && (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
            "& *": {
              fontFamily: "Inter, Oxygen, Roboto, sans-serif",
            },
          }}
        >
          <Table
            sx={{
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--terbine-blue)",
                  "& th": {
                    color: "white",
                    fontSize: "clamp(12px, 1.5vw, 18px)",
                    fontWeight: 300,
                    borderBottom: "none",
                  },
                  borderBottom: "none",
                }}
              >
                {[
                  { label: "Type", width: "15%" },
                  { align: "center", label: "Status", width: "3%" },
                  { label: "API Token" },
                ].map((header, i) => (
                  <TableCell
                    align={header.align || "left"}
                    key={i}
                    width={header.width || null}
                  >
                    {header.label}
                  </TableCell>
                ))}
                <TableCell width="3%" />
                <TableCell width="3%" />
                <TableCell width="10%" />
                <TableCell width="3%" />
                <TableCell width="3%" />
              </TableRow>
            </TableHead>
            <TableBody>
              {apiTableTestData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: "#F2F2F2",
                    borderBottom:
                      index !== apiTableTestData.length - 1
                        ? "3px solid white"
                        : "none",
                    borderTop: "none",
                    "& > td": {
                      borderTop: "none",
                      borderBottom:
                        index !== apiTableTestData.length - 1
                          ? "3px solid white"
                          : "none",
                      color: "black",
                    },
                  }}
                >
                  <TableCell>{row.type}</TableCell>
                  <TableCell align="center">
                    <div
                      style={{
                        background: `${
                          row.status === "Online" ? "limegreen" : "white"
                        }`,
                        border: `${
                          row.status === "Offline" ? "1px solid #D9D9D9" : null
                        }`,
                        borderRadius: "50%",
                        height: "16px",
                        margin: "auto",
                        width: "16px",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div
                      style={{
                        background: "white",
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                        fontFamily: "Monaco, Courier New, monospace",
                        padding: "8px",
                      }}
                    >
                      {row.apiToken}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ cursor: "pointer" }}>
                      <RefreshIcon />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ cursor: "pointer" }}>
                      <CopyFileIcon />
                    </div>
                  </TableCell>
                  <TableCell>
                    <SelectFeedModal />
                  </TableCell>
                  <TableCell>
                    <div style={{ cursor: "pointer" }}>
                      <EditIcon />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ cursor: "pointer" }}>
                      <CloseIcon />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default Technical;
